import React, { useState, useEffect } from "react";
import {
  AppBar,
  Toolbar,
  Box,
  Typography,
  IconButton,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  Collapse,
  Menu,
  MenuItem,
  useMediaQuery,
  useTheme,
  styled,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import EmailIcon from "@mui/icons-material/Email";
import CallIcon from "@mui/icons-material/Call";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { motion, AnimatePresence, useScroll, useSpring } from "framer-motion";
import { Helmet } from "react-helmet-async";

// Styled Components
const HeaderWrapper = styled(AppBar)({
  background: "rgba(10, 10, 20, 0.22)",
  backdropFilter: "blur(8px)",
  WebkitBackdropFilter: "blur(8px)",
  boxShadow: "none",
  transition: "background 0.35s ease, backdrop-filter 0.35s ease, padding 0.35s ease",
  "&.scrolled": {
    background: "rgba(8, 8, 16, 0.78)",
    backdropFilter: "blur(20px)",
    WebkitBackdropFilter: "blur(20px)",
    boxShadow: "0 8px 30px rgba(0,0,0,0.25)",
  },
});

const NavPill = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "2px",
  padding: "6px",
  borderRadius: 999,
  background: "rgba(255,255,255,0.07)",
  border: "1px solid rgba(255,255,255,0.12)",
  backdropFilter: "blur(10px)",
});

const NavLink = styled(motion.div)({
  cursor: "pointer",
  color: "#fff",
  padding: "8px 18px",
  borderRadius: 999,
  position: "relative",
  zIndex: 1,
  whiteSpace: "nowrap",
});

const Logo = styled(Typography)({
  fontFamily: "Lobster, cursive",
  fontSize: "1.7rem",
  background: "linear-gradient(90deg, #FFFFFF, #FFD9A0)",
  WebkitBackgroundClip: "text",
  backgroundClip: "text",
  WebkitTextFillColor: "transparent",
  color: "transparent",
  cursor: "pointer",
  transition: "transform 0.3s ease",
  "&:hover": {
    transform: "scale(1.05)",
  },
});

const ScrollProgress = styled(motion.div)({
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  height: 3,
  background: "linear-gradient(90deg, #FD8112, #FF5F6D)",
  transformOrigin: "0% 50%",
  zIndex: 1301,
});

const GlassIconButton = styled(IconButton)({
  color: "#fff",
  background: "rgba(255,255,255,0.08)",
  border: "1px solid rgba(255,255,255,0.14)",
  backdropFilter: "blur(10px)",
  transition: "transform 0.3s ease, background 0.3s ease, box-shadow 0.3s ease",
  "&:hover": {
    background: "rgba(253,129,18,0.85)",
    transform: "translateY(-2px)",
    boxShadow: "0 10px 24px rgba(253,129,18,0.4)",
  },
});

export default function Header() {
  const [anchorEl, setAnchorEl] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { scrollYProgress } = useScroll();
  const progressWidth = useSpring(scrollYProgress, { stiffness: 200, damping: 30 });

  const menuItems = [
    { label: "Home", path: "/" },
    { label: "Blog", path: "/blog" },
    { label: "About Us", path: "/aboutus" },
    { label: "Contact", path: "/contact" },
    { label: "Company", path: "/company" },
  ];

  const serviceRoutes = {
    "Search Engine Optimization": "/services/seo",
    "Social Media Marketing": "/services/social-media-marketing",
    "Google Ads": "/services/google-ads",
    "Website Designing": "/services/website-designing",
    "App Development": "/services/app-development",
    "Influencer Marketing": "/services/influencer-marketing",
    "WhatsApp Marketing": "/services/whatsapp-marketing",
    "Online Reputation Management": "/services/orm",
    "PR Agency": "/services/pr-agency",
    "Email Marketing": "/services/email-marketing",
  };

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleNavigate = (path) => {
    navigate(path);
    setMobileOpen(false);
    setAnchorEl(null);
  };

  const handleServiceClick = (service) => {
    navigate(serviceRoutes[service]);
    setAnchorEl(null);
    setMobileOpen(false);
  };

  const drawer = (
    <Box
      sx={{
        textAlign: "center",
        pt: 2,
        height: "100%",
        color: "#fff",
        background: "linear-gradient(165deg, rgba(12,12,20,0.98), rgba(30,18,10,0.98))",
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "flex-end", pr: 2, mb: 1 }}>
        <IconButton onClick={() => setMobileOpen(false)} sx={{ color: "#fff" }} aria-label="Close menu">
          <CloseIcon />
        </IconButton>
      </Box>
      <List>
        {menuItems.map((item, idx) => (
          <motion.div
            key={item.label}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.05, duration: 0.35 }}
          >
            <ListItemButton
              component={Link}
              to={item.path}
              selected={location.pathname === item.path}
              onClick={() => setMobileOpen(false)}
            >
              <ListItemText
                primary={
                  <Typography
                    sx={{
                      color: location.pathname === item.path ? "#FD8112" : "#fff",
                      fontWeight: location.pathname === item.path ? 600 : 400,
                    }}
                  >
                    {item.label}
                  </Typography>
                }
              />
            </ListItemButton>
          </motion.div>
        ))}

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: menuItems.length * 0.05, duration: 0.35 }}
        >
          <Box onClick={() => setServicesOpen(!servicesOpen)} role="button" tabIndex={0}>
            <ListItemButton>
              <ListItemText primary={<Typography sx={{ color: "#FD8112", fontWeight: 500 }}>Services</Typography>} />
              <ExpandMoreIcon
                sx={{
                  color: "#fff",
                  transform: servicesOpen ? "rotate(180deg)" : "none",
                  transition: "0.3s",
                }}
              />
            </ListItemButton>
          </Box>
          <Collapse in={servicesOpen}>
            <List component="div" disablePadding>
              {Object.keys(serviceRoutes).map((service) => (
                <ListItemButton key={service} sx={{ pl: 4 }} onClick={() => handleServiceClick(service)}>
                  <ListItemText primary={<Typography sx={{ color: "#fff" }}>{service}</Typography>} />
                </ListItemButton>
              ))}
            </List>
          </Collapse>
        </motion.div>
      </List>
    </Box>
  );

  return (
    <>
      <Helmet>
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "SiteNavigationElement",
            "name": ["Home", "Blog", "About Us", "Contact", "Company", "Services"],
            "url": [
              "https://www.slgt.in/",
              "https://www.slgt.in/blog",
              "https://www.slgt.in/aboutus",
              "https://www.slgt.in/contact",
              "https://www.slgt.in/company",
              "https://www.slgt.in/services"
            ]
          }
        `}</script>
      </Helmet>

      <ScrollProgress style={{ scaleX: progressWidth }} />

      <HeaderWrapper position="fixed" className={scrolled ? "scrolled" : ""}>
        <nav aria-label="Main navigation">
          <Toolbar
            sx={{
              display: "flex",
              justifyContent: "space-between",
              px: { xs: 2, md: 3 },
              minHeight: scrolled ? { xs: 56, md: 64 } : { xs: 64, md: 76 },
              transition: "min-height 0.35s ease",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              {isMobile && (
                <GlassIconButton
                  onClick={() => setMobileOpen(true)}
                  aria-label="Open menu"
                  sx={{ mr: 1 }}
                >
                  <MenuIcon />
                </GlassIconButton>
              )}
              <Box
                component={Link}
                to="/"
                sx={{ display: "flex", alignItems: "center", gap: 1, textDecoration: "none" }}
              >
                <Box
                  component="img"
                  src={`${process.env.PUBLIC_URL}/assets/slgt-logo.png`}
                  alt="SLGT logo"
                  loading="lazy"
                  sx={{
                    height: { xs: 34, md: 40 },
                    width: "auto",
                    borderRadius: "8px",
                    filter: "drop-shadow(0 4px 10px rgba(0,0,0,0.35))",
                  }}
                />
                <Logo>SLGT</Logo>
              </Box>
            </Box>

            {!isMobile && (
              <NavPill onMouseLeave={() => setHoveredIndex(null)}>
                {menuItems.map((item, idx) => (
                  <Box key={item.label} sx={{ position: "relative" }} onMouseEnter={() => setHoveredIndex(idx)}>
                    <AnimatePresence>
                      {hoveredIndex === idx && (
                        <motion.div
                          layoutId="nav-hover-pill"
                          style={{
                            position: "absolute",
                            inset: 0,
                            borderRadius: 999,
                            background: "rgba(255,255,255,0.14)",
                          }}
                          transition={{ type: "spring", stiffness: 320, damping: 28 }}
                        />
                      )}
                    </AnimatePresence>
                    <NavLink
                      onClick={() => handleNavigate(item.path)}
                      style={{
                        fontWeight: location.pathname === item.path ? 600 : 400,
                        color: location.pathname === item.path ? "#ff9800" : "#fff",
                      }}
                    >
                      {item.label}
                    </NavLink>
                  </Box>
                ))}
                <Box
                  sx={{ position: "relative", display: "flex", alignItems: "center" }}
                  onMouseEnter={() => setHoveredIndex(menuItems.length)}
                >
                  <AnimatePresence>
                    {hoveredIndex === menuItems.length && (
                      <motion.div
                        layoutId="nav-hover-pill"
                        style={{
                          position: "absolute",
                          inset: 0,
                          borderRadius: 999,
                          background: "rgba(255,255,255,0.14)",
                        }}
                        transition={{ type: "spring", stiffness: 320, damping: 28 }}
                      />
                    )}
                  </AnimatePresence>
                  <NavLink
                    onClick={() => navigate("/services")}
                    style={{
                      fontWeight: location.pathname.startsWith("/services") ? 600 : 400,
                      color: location.pathname.startsWith("/services") ? "#ff9800" : "#fff",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    Services
                  </NavLink>
                  <IconButton
                    size="small"
                    aria-label="Open services dropdown"
                    aria-haspopup="true"
                    aria-expanded={Boolean(anchorEl)}
                    onClick={(e) => {
                      e.stopPropagation();
                      setAnchorEl(e.currentTarget);
                    }}
                    sx={{ color: "white", ml: -0.5, mr: 0.5, zIndex: 1 }}
                  >
                    <ExpandMoreIcon fontSize="small" />
                  </IconButton>
                </Box>
              </NavPill>
            )}

            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <GlassIconButton
                component="a"
                href="mailto:slgtmarketingagency@gmail.com"
                aria-label="Send email to SLGT"
                size={isMobile ? "small" : "medium"}
              >
                <EmailIcon fontSize={isMobile ? "small" : "medium"} />
              </GlassIconButton>
              <GlassIconButton
                onClick={() => navigate("/contact")}
                aria-label="Call SLGT"
                size={isMobile ? "small" : "medium"}
              >
                <CallIcon fontSize={isMobile ? "small" : "medium"} />
              </GlassIconButton>
            </Box>
          </Toolbar>
        </nav>

        <Menu
          id="services-menu"
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          PaperProps={{
            sx: {
              mt: 1,
              borderRadius: 3,
              backdropFilter: "blur(20px)",
              background: "rgba(20,20,28,0.92)",
              color: "#fff",
            },
          }}
        >
          {Object.keys(serviceRoutes).map((service) => (
            <MenuItem
              key={service}
              onClick={() => handleServiceClick(service)}
              sx={{ "&:hover": { background: "rgba(253,129,18,0.18)" } }}
            >
              {service}
            </MenuItem>
          ))}
        </Menu>

        <Drawer
          anchor="left"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          PaperProps={{ sx: { width: "100%", maxWidth: "100%" } }}
        >
          {drawer}
        </Drawer>
      </HeaderWrapper>
    </>
  );
}
